import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiFetch, API_CONFIG } from '../config/api';
import './NotificationBell.css';

const NOTIFICATIONS_URL = `${API_CONFIG.BASE_URL}/api/notifications`;
const POLL_INTERVAL = 60000; // 60 seconds

const NotificationBell = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await apiFetch(`${NOTIFICATIONS_URL}/unread-count`);
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.unread_count);
      }
    } catch {
      // Silently ignore polling errors
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${NOTIFICATIONS_URL}?limit=20`);
      if (res.ok) {
        const data = await res.json();
        setNotifications(data);
      }
    } catch {
      // Ignore
    } finally {
      setLoading(false);
    }
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      await apiFetch(`${NOTIFICATIONS_URL}/${notificationId}/read`, { method: 'PATCH' });
      setNotifications(prev =>
        prev.map(n => n.notification_id === notificationId ? { ...n, is_read: 1 } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      // Ignore
    }
  };

  const markAllRead = async () => {
    try {
      await apiFetch(`${NOTIFICATIONS_URL}/mark-all-read`, { method: 'PATCH' });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
      setUnreadCount(0);
    } catch {
      // Ignore
    }
  };

  // Poll for unread count
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Fetch full list when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d`;
  };

  const typeIcon = (type) => {
    switch (type) {
      case 'SUCCESS': return '[OK]';
      case 'WARNING': return '[!!]';
      case 'ERROR': return '[ERR]';
      case 'SHIPMENT': return '[SHP]';
      default: return '[i]';
    }
  };

  return (
    <div className="notification-bell-container" ref={dropdownRef}>
      <span
        className="notification-bell"
        onClick={() => setIsOpen(!isOpen)}
        title={t('notificationBell.title')}
      >
        {unreadCount > 0 ? `[${unreadCount}]` : '[ ]'}
      </span>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-dropdown-header">
            <span>{t('notificationBell.title')}</span>
            {unreadCount > 0 && (
              <button className="mark-all-read-btn" onClick={markAllRead}>
                {t('notificationBell.markAllRead')}
              </button>
            )}
          </div>

          <div className="notification-list">
            {loading && <div className="notification-loading">{t('ui.loading')}</div>}
            {!loading && notifications.length === 0 && (
              <div className="notification-empty">{t('notificationBell.empty')}</div>
            )}
            {!loading && notifications.map(n => (
              <div
                key={n.notification_id}
                className={`notification-item ${n.is_read ? 'read' : 'unread'}`}
                onClick={() => {
                  if (!n.is_read) markAsRead(n.notification_id);
                  if (n.link_url) {
                    // External URLs get full redirect; internal paths use React Router
                    if (n.link_url.startsWith('http')) {
                      window.location.href = n.link_url;
                    } else {
                      navigate(n.link_url);
                    }
                  }
                }}
              >
                <span className={`notification-type type-${(n.notification_type || 'INFO').toLowerCase()}`}>
                  {typeIcon(n.notification_type)}
                </span>
                <div className="notification-content">
                  <div className="notification-title">{n.title}</div>
                  <div className="notification-message">{n.message}</div>
                </div>
                <span className="notification-time">{formatTime(n.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
